import { useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import {
  LIST_CONTENT,
  GET_CONTENT,
  CREATE_CONTENT,
  UPDATE_CONTENT,
  DELETE_CONTENT,
} from '../graphql/operations';
import useApi from './useApi';

// Hook for listing content with Apollo
export function useListContent(variables = {}) {
  const { data, loading, error, fetchMore, refetch } = useQuery(LIST_CONTENT, {
    variables: { input: variables },
  });

  return {
    items: data?.listContent?.items || [],
    loading,
    error,
    nextToken: data?.listContent?.nextToken,
    refetch,
    fetchMore: () => {
      if (data?.listContent?.nextToken) {
        fetchMore({
          variables: {
            input: {
              ...variables,
              nextToken: data.listContent.nextToken,
            },
          },
        });
      }
    },
  };
}

// Hook for getting single content with Apollo
export function useGetContent(contentId) {
  const { data, loading, error, refetch } = useQuery(GET_CONTENT, {
    variables: { contentId },
    skip: !contentId,
  });

  return {
    item: data?.getContent?.item || null,
    versionHistory: data?.getContent?.versionHistory || [],
    loading,
    error,
    refetch,
  };
}

// Hook for creating content
export function useCreateContent() {
  const [createContent, { loading, error }] = useMutation(CREATE_CONTENT, {
    refetchQueries: [{ query: LIST_CONTENT, variables: { input: {} } }],
  });

  const execute = async (input) => {
    const { data } = await createContent({
      variables: { input },
    });
    return data.createContent;
  };

  return { execute, loading, error };
}

// Hook for updating content
export function useUpdateContent() {
  const [updateContent, { loading, error }] = useMutation(UPDATE_CONTENT);

  const execute = async (contentId, input) => {
    const { data } = await updateContent({
      variables: { contentId, input },
      refetchQueries: [
        { query: LIST_CONTENT, variables: { input: {} } },
        { query: GET_CONTENT, variables: { contentId } },
      ],
    });
    return data.updateContent;
  };

  return { execute, loading, error };
}

// Hook for deleting content
export function useDeleteContent() {
  const [deleteContent, { loading, error }] = useMutation(DELETE_CONTENT, {
    refetchQueries: [{ query: LIST_CONTENT, variables: { input: {} } }],
  });

  const execute = async (contentId) => {
    const { data } = await deleteContent({
      variables: { contentId },
    });
    return data.deleteContent;
  };

  return { execute, loading, error };
}

// Legacy default export with REST API fallback for backward compatibility
export default function useContent() {
  const api = useApi();

  const listContent = useCallback(
    ({ type, status, limit, nextToken } = {}, forceRefresh = false) => {
      const params = new URLSearchParams();
      if (type) params.set('type', type);
      if (status) params.set('status', status);
      if (limit) params.set('limit', limit);
      if (nextToken) params.set('nextToken', nextToken);
      const query = params.toString();
      return api.get(`/content${query ? `?${query}` : ''}`, forceRefresh);
    },
    [api],
  );

  const getContent = useCallback((contentId, forceRefresh = false) => api.get(`/content/${contentId}`, forceRefresh), [api]);
  const createContent = useCallback((payload) => api.post('/content', payload), [api]);
  const updateContent = useCallback((contentId, payload) => api.put(`/content/${contentId}`, payload), [api]);
  const deleteContent = useCallback((contentId) => api.del(`/content/${contentId}`), [api]);

  return useMemo(
    () => ({
      listContent,
      getContent,
      createContent,
      updateContent,
      deleteContent,
    }),
    [listContent, getContent, createContent, updateContent, deleteContent],
  );
}
